import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { AccessibleChart } from '../../components/charts/AccessibleChart';
import { api } from '../../lib/api';
import { formatAmount } from '../../lib/format';
import { useWorkspace } from '../../hooks/useWorkspace';

interface TrialBalanceRow {
  account_id: string; code: string; name: string; kind: string;
  debit_cents: string; credit_cents: string;
}

interface ApInvoice {
  id: string;
  status: 'pending_review' | 'approved' | 'scheduled' | 'paid' | 'disputed' | 'void';
  total_cents: string;
  currency: string;
  due_date: string | null;
  vendor_id: string | null;
}

interface SpendChangeRow {
  code: string;
  name: string;
  priorCents: number;
  currentCents: number;
  deltaCents: number;
}

/** First/last day (YYYY-MM) of a month offset back from a reference date. */
function monthRange(ref: Date, monthsAgo: number): { from: string; to: string; ym: string } {
  const y = ref.getUTCFullYear();
  const m = ref.getUTCMonth() - monthsAgo;
  const first = new Date(Date.UTC(y, m, 1));
  const last = new Date(Date.UTC(y, m + 1, 0));
  return {
    from: first.toISOString().slice(0, 10),
    to: last.toISOString().slice(0, 10),
    ym: first.toISOString().slice(0, 7),
  };
}

export function ReportsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { data: ws } = useWorkspace(wsId);
  const currency = ws?.base_currency ?? 'JPY';

  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);

  const tb = useQuery<TrialBalanceRow[]>({
    queryKey: ['trial-balance', wsId, from, to],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/ledger/trial-balance?from=${from}&to=${to}`)).data,
  });

  const ap = useQuery<ApInvoice[]>({
    queryKey: ['ap-for-reports', wsId],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/ap-invoices?limit=500`)).data,
  });

  // Spend-change waterfall: default current = previous full month, prior = the month before.
  const defaultCurrent = monthRange(now, 1);
  const defaultPrior = monthRange(now, 2);
  const [priorYm, setPriorYm] = useState(defaultPrior.ym);
  const [currentYm, setCurrentYm] = useState(defaultCurrent.ym);

  const ymToRange = (ym: string) => {
    const [yy, mm] = ym.split('-').map(Number);
    const first = new Date(Date.UTC(yy, mm - 1, 1));
    const last = new Date(Date.UTC(yy, mm, 0));
    return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
  };
  const priorRange = ymToRange(priorYm);
  const currentRange = ymToRange(currentYm);

  const spend = useQuery<SpendChangeRow[]>({
    queryKey: ['spend-change', wsId, priorYm, currentYm],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get(
        `/workspaces/${wsId}/ledger/spend-change?fromA=${priorRange.from}&toA=${priorRange.to}&fromB=${currentRange.from}&toB=${currentRange.to}`,
      )).data,
  });

  const spendWaterfall = useMemo(
    () =>
      (spend.data ?? [])
        .slice(0, 10)
        .map((r) => ({ label: r.name, value: r.deltaCents })),
    [spend.data],
  );

  const totalDebit = (tb.data ?? []).reduce((s, r) => s + Number(r.debit_cents), 0);
  const totalCredit = (tb.data ?? []).reduce((s, r) => s + Number(r.credit_cents), 0);

  // Chart 1: AP aging buckets — current / 1-30 / 31-60 / 61-90 / 90+ days past due.
  const apAging = useMemo(() => {
    const buckets = { current: 0, b1: 0, b2: 0, b3: 0, b4: 0 };
    const today = new Date();
    for (const inv of ap.data ?? []) {
      if (inv.status === 'paid' || inv.status === 'void') continue;
      if (!inv.due_date) continue;
      const days = Math.floor((today.getTime() - new Date(inv.due_date).getTime()) / (1000 * 60 * 60 * 24));
      const v = Number(inv.total_cents);
      if (days < 0) buckets.current += v;
      else if (days <= 30) buckets.b1 += v;
      else if (days <= 60) buckets.b2 += v;
      else if (days <= 90) buckets.b3 += v;
      else buckets.b4 += v;
    }
    return [
      { label: t('reports.aging.current'), value: buckets.current },
      { label: '1–30', value: buckets.b1 },
      { label: '31–60', value: buckets.b2 },
      { label: '61–90', value: buckets.b3 },
      { label: '90+', value: buckets.b4 },
    ];
  }, [ap.data, t]);

  // Chart 2: Expense by account (top 8 by net) — pulled from trial balance.
  const expenseByAccount = useMemo(() => {
    return (tb.data ?? [])
      .filter((r) => r.kind === 'expense')
      .map((r) => ({ label: r.name, value: Number(r.debit_cents) - Number(r.credit_cents) }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [tb.data]);

  const formatCents = (n: number) => formatAmount(n, currency, i18n.language);

  return (
    <AppShell>
      <a
        href="#reports-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:dark:bg-ink-900 focus:px-3 focus:py-2 focus:rounded focus:border focus:border-ink-300"
      >
        {t('a11y.skipToContent')}
      </a>
      <div id="reports-main" className="px-8 py-7 max-w-6xl">
        <h1 className="display text-2xl font-semibold mb-1">{t('reports.title')}</h1>
        <p className="text-sm text-ink-500 mb-6">{t('reports.currentMonthTbHeader', { from, to })}</p>

        <div className="mb-6 grid lg:grid-cols-2 gap-4">
          <AiInsightCard
            agent="spend_analysis"
            title={t('aiOutput.cards.spendAnalysis')}
            prompt={t('aiOutput.prompts.spendAnalysis')}
          />
          <AiInsightCard
            agent="cost_reduction"
            title={t('aiOutput.cards.costReduction')}
            prompt={t('aiOutput.prompts.costReduction')}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <Card>
            <CardHeader><div className="font-medium">{t('reports.apAging')}</div></CardHeader>
            <CardBody>
              <AccessibleChart
                title={t('reports.apAging')}
                summary={apAging.map((b) => `${b.label}: ${formatCents(b.value)}`).join(', ')}
                series={[{ name: t('reports.outstanding'), data: apAging }]}
                type="bar"
                valueFormatter={formatCents}
              />
            </CardBody>
          </Card>
          <Card>
            <CardHeader><div className="font-medium">{t('reports.expenseByAccount')}</div></CardHeader>
            <CardBody>
              {expenseByAccount.length === 0 ? (
                <div className="text-center text-ink-500 text-sm py-10">{t('reports.noExpenses')}</div>
              ) : (
                <AccessibleChart
                  title={t('reports.expenseByAccount')}
                  summary={expenseByAccount.map((x) => `${x.label}: ${formatCents(x.value)}`).join(', ')}
                  series={[{ name: t('reports.expense'), data: expenseByAccount }]}
                  type="pie"
                  valueFormatter={formatCents}
                />
              )}
            </CardBody>
          </Card>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-medium">{t('reports.spendWaterfall')}</div>
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1.5 text-ink-500">
                  {t('reports.periodPrior')}
                  <input
                    type="month"
                    value={priorYm}
                    onChange={(e) => setPriorYm(e.target.value)}
                    className="h-8 px-2 rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-800 dark:text-ink-100"
                  />
                </label>
                <label className="flex items-center gap-1.5 text-ink-500">
                  {t('reports.periodCurrent')}
                  <input
                    type="month"
                    value={currentYm}
                    onChange={(e) => setCurrentYm(e.target.value)}
                    className="h-8 px-2 rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-ink-800 dark:text-ink-100"
                  />
                </label>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {spend.isLoading ? (
              <div className="text-center text-ink-500 text-sm py-10">{t('common.loading')}</div>
            ) : spendWaterfall.length === 0 ? (
              <div className="text-center text-ink-500 text-sm py-10">{t('reports.noExpenses')}</div>
            ) : (
              <AccessibleChart
                title={t('reports.spendWaterfall')}
                summary={t('reports.spendWaterfallSummary', {
                  count: spendWaterfall.length,
                  top: spendWaterfall[0]?.label ?? '',
                  delta: formatCents(spendWaterfall[0]?.value ?? 0),
                })}
                series={[{ name: t('reports.spendWaterfall'), data: spendWaterfall }]}
                type="waterfall"
                valueFormatter={formatCents}
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader><div className="font-medium">{t('reports.trialBalance')}</div></CardHeader>
          {tb.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : tb.data?.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('reports.noEntries')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-ink-500 border-b border-ink-200 dark:border-ink-800">
                    <th className="px-5 py-3 font-medium">{t('reports.account')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('ledger.debit')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('ledger.credit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tb.data?.map((r) => (
                    <tr key={r.account_id} className="border-b border-ink-200 dark:border-ink-800">
                      <td className="px-5 py-2.5">
                        <div className="font-mono text-xs text-ink-400">{r.code}</div>
                        <div>{r.name}</div>
                      </td>
                      <td className="px-5 py-2.5 text-right num">{formatAmount(r.debit_cents, currency, i18n.language)}</td>
                      <td className="px-5 py-2.5 text-right num">{formatAmount(r.credit_cents, currency, i18n.language)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-ink-50 dark:bg-ink-900">
                    <td className="px-5 py-3">{t('common.total')}</td>
                    <td className="px-5 py-3 text-right num">{formatAmount(totalDebit, currency, i18n.language)}</td>
                    <td className="px-5 py-3 text-right num">{formatAmount(totalCredit, currency, i18n.language)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
